import type {
  DashboardSectionDescriptor,
  DashboardSectionKey
} from 'constant/dashboardSections';
import {
  DASHBOARD_SECTION_BY_FOOTER_ID,
  DASHBOARD_SECTION_BY_ROUTE
} from 'constant/dashboardSections';

import { ROUTES } from 'constant/routes';

import type { SystemModulesResponse, TabType } from 'types';

/** Matches dashboard nav links without false positives (e.g. `/a/pr` vs `/a/profile`). */
export const isDashboardNavLinkActive = (
  pathname: string,
  link: string
): boolean => {
  if (link === ROUTES.dashboard) {
    return pathname === ROUTES.dashboard;
  }

  return pathname === link || pathname.startsWith(`${link}/`);
};

export const findActiveDashboardNavTab = (
  pathname: string,
  navTabs: TabType[] = []
): TabType | undefined =>
  navTabs
    .filter(({ link }) => isDashboardNavLinkActive(pathname, link))
    .sort((left, right) => right.link.length - left.link.length)[0];

export const getActiveDashboardNavTabIndex = (
  pathname: string,
  navTabs: TabType[] = []
): number => {
  const activeTab = findActiveDashboardNavTab(pathname, navTabs);

  if (!activeTab) {
    return -1;
  }

  return navTabs.findIndex(({ link }) => link === activeTab.link);
};

/**
 * Resolves whether a section is enabled, per the fail-open policy: while sys-modules are
 * loading, once sys-modules has been written off for the session (`isFallback` — a 400, a
 * timeout, an unrecognised payload), or when a tenant's backend simply hasn't shipped a given
 * `show_acct_*` field yet, the section is treated as enabled. Only an explicit `false`/`0`
 * from a payload we understood hides it. Sections not registered in
 * `DASHBOARD_SECTION_BY_KEY` (e.g. My Profile, My Dashboard, or any tenant-custom link) are
 * always enabled.
 */
export const resolveSectionEnabled = (
  descriptor: DashboardSectionDescriptor | undefined,
  sysModules: SystemModulesResponse | undefined,
  isLoading: boolean,
  isFallback: boolean
): boolean => {
  if (!descriptor) return true;
  if (isLoading || isFallback || !sysModules) return true;

  const raw = sysModules[descriptor.sysPrefField];
  return raw !== false && raw !== 0;
};

/** Filters a tab/link array (Tabs, Menu, ProfileSidebar) by nav-route section visibility. */
export const filterDashboardNavItems = <T extends { link: string }>(
  items: T[] | undefined,
  isSectionEnabled: (key: DashboardSectionKey) => boolean
): T[] =>
  (items ?? []).filter((item) => {
    const key = DASHBOARD_SECTION_BY_ROUTE[item.link];
    return !key || isSectionEnabled(key);
  });

/** Filters a tenant's `footerMenu` array by `id`-matched section visibility. */
export const filterFooterMenuItems = <T extends { id?: string }>(
  items: T[] | undefined,
  isSectionEnabled: (key: DashboardSectionKey) => boolean
): T[] =>
  (items ?? []).filter((item) => {
    const key = item.id ? DASHBOARD_SECTION_BY_FOOTER_ID[item.id] : undefined;
    return !key || isSectionEnabled(key);
  });
